/** Pricing types hidden from asset create / edit (hourly, daily, and monthly remain). */

const PRICING_NAME_KEYS = ['pricing_type_name', 'type_name', 'name', 'label', 'title'] as const;

function normalizePricingTypeLabel(label: string): string {
  return label.trim().toLowerCase().replace(/\s+/g, ' ');
}

function pricingTypeRowLabel(row: Record<string, unknown>): string {
  for (const key of PRICING_NAME_KEYS) {
    const v = row[key];
    if (typeof v === 'string' && v.trim()) {
      return v.trim();
    }
  }
  return '';
}

const EXCLUDED_PRICING_LABELS = new Set([
  'week',
  'weekly',
  'per week',
  'weekly rate',
  'year',
  'yearly',
  'annual',
  'per year',
  'yearly rate',
]);

/** Hebrew labels from GET `/assets/pricing-types`. */
const EXCLUDED_PRICING_HEBREW = /שבועי|שנתי/;

export function isExcludedAssetCreatePricingType(row: Record<string, unknown>): boolean {
  const raw = pricingTypeRowLabel(row);
  if (!raw) {
    return false;
  }
  if (EXCLUDED_PRICING_HEBREW.test(raw)) {
    return true;
  }
  const normalized = normalizePricingTypeLabel(raw);
  if (EXCLUDED_PRICING_LABELS.has(normalized)) {
    return true;
  }
  if (/\b(week|year|annual)\b/.test(normalized)) {
    return true;
  }
  return false;
}

export function filterPricingTypesForAssetCreate(
  rows: Record<string, unknown>[],
): Record<string, unknown>[] {
  return rows.filter((row) => !isExcludedAssetCreatePricingType(row));
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

function pricingTypeIdFromRow(row: Record<string, unknown>): number | null {
  const raw = row.pricing_type_id ?? row.id;
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return raw;
  }
  if (typeof raw === 'string' && raw.trim()) {
    const n = Number(raw.trim());
    if (Number.isFinite(n)) {
      return n;
    }
  }
  return null;
}

/** Drop weekly / yearly selections from persisted create or edit bodies. */
export function stripDisallowedPricingMethodsFromAssetBody(
  body: Record<string, unknown>,
  allowedCatalog: Record<string, unknown>[],
): Record<string, unknown> {
  const methods = body.pricing_methods;
  if (!Array.isArray(methods) || methods.length === 0 || allowedCatalog.length === 0) {
    return body;
  }
  const allowed = new Set(
    allowedCatalog
      .map((row) => pricingTypeIdFromRow(row))
      .filter((id): id is number => id !== null),
  );
  const kept = methods.filter((item) => {
    if (!isPlainObject(item)) {
      return false;
    }
    const id = pricingTypeIdFromRow(item);
    return id !== null && allowed.has(id);
  });
  if (kept.length === methods.length) {
    return body;
  }
  if (kept.length === 0) {
    const next = { ...body };
    delete next.pricing_methods;
    return next;
  }
  return { ...body, pricing_methods: kept };
}
